<template>
  <div class="login-container">
    <!-- Left side - Login Form -->
    <div class="login-form-section">
      <div class="logo-container">
        <v-img :src="loginLogo" width="139" contain>
          <v-layout slot="placeholder" fill-height align-center justify-center ma-0>
            <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
          </v-layout>
        </v-img>
      </div>

      <div class="welcome-text">
        <h2>Welcome Back 👋</h2>
      </div>

      <v-card flat class="login-card" v-if="!showEnterPIN">
        <v-form v-model="loginForm" ref="loginForm" lazy-validation>
          <div class="form-group">
            <label class="form-label">Email</label>
            <v-text-field
              @keyup.enter="login_username_password"
              :rules="emailRules"
              required
              v-model="email"
              placeholder="Example@email.com"
              type="text"
              outlined
              dense
              hide-details
              class="custom-input"
              prepend-icon="mail"
            ></v-text-field>
          </div>

          <div class="form-group">
            <label class="form-label">Password</label>
            <v-text-field
              @keyup.enter="login_username_password"
              :rules="basicRules"
              required
              v-model="password"
              placeholder="Password"
              outlined
              dense
              hide-details
              class="custom-input"
              prepend-icon="lock"
              :append-icon="pwordPrependIcon ? 'visibility' : 'visibility_off'"
              @click:append="pwordPrependIcon = !pwordPrependIcon"
              :type="pwordPrependIcon ? 'password' : 'text'"
            ></v-text-field>
          </div>

          <div class="forgot-password">
            <a @click.prevent="goToForgotPassword" class="forgot-link">Forgot Password?</a>
          </div>
          <!--

	<v-card-actions>
				<v-btn color="secondary" @click="login_groupdevices" class="white--text" :loading="buttonLoading"
					:disabled="buttonLoading">Device
					Group Dashboard
					<v-icon right>apps</v-icon>
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn color="peterBlue" @click="login_username_password" class="white--text" :loading="buttonLoading"
					:disabled="buttonLoading">Login
					<v-icon right>send</v-icon>
				</v-btn>
			</v-card-actions>
-->
          <v-btn
            color="#00BFFF"
            @click="login_username_password"
            class="login-btn"
            :loading="buttonLoading"
            :disabled="buttonLoading"
            block
            large
            depressed
          >
            Login
          </v-btn>
        </v-form>
        <!-- Sign in with Okta section -->
        <v-divider class="mt-3"></v-divider>
        <v-card-actions class="justify-center">
          <v-btn
            color="orange darken-2"
            @click="initiateOktaLogin"
            class="white--text"
            :loading="oktaLoading"
            :disabled="oktaLoading || buttonLoading"
          >
            <v-icon left>account_circle</v-icon>
            Sign in with Okta
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card flat class="login-card" v-else>
        <div class="welcome-text">
          <h3>Authenticator Code</h3>
        </div>
        <v-form v-model="loginForm" ref="loginForm" lazy-validation>
          <div class="form-group">
            <label class="form-label">OTP</label>
            <v-text-field
              @keyup.enter="login_mfa"
              required
              v-model="pin"
              placeholder="OTP"
              type="text"
              outlined
              dense
              hide-details
              class="custom-input"
              prepend-icon="key"
            ></v-text-field>
          </div>

          <v-btn
            type="submit"
            color="#00BFFF"
            @click="login_mfa"
            class="login-btn"
            :loading="buttonLoading"
            :disabled="buttonLoading"
            block
            large
            depressed
          >
            Login
          </v-btn>
        </v-form>
      </v-card>
    </div>

    <!-- Right side - Promotional Content -->
    <div class="promo-section"></div>

    <v-snackbar top right v-model="snackbar" :color="snackbarColor">
      {{ snackbarMessage }}
      <v-btn flat color="white" @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import mixins from '@/mixins';
import { VENUE_STORAGE, USER_ROLES } from '@/constants';
import { getSelectedVenue } from '@/services/helpers';
import { VENUE_STORAGE_USED } from '../constants';

export default {
  data: () => ({
    user: null,
    email: null,
    password: null,
    pin: null,
    loginForm: true,
    buttonLoading: false,
    pwordPrependIcon: true,
    snackbarColor: null,
    landingModal: false,
    showEnterPIN: false,
    oktaLoading: false
  }),
  async mounted() {
    // Check if this is an Okta callback
    if (this.$route.meta.isOktaCallback) {
      await this.handleOktaCallback();
    }

    // Add keydown listener for Enter
    window.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        const btn = document.getElementById('login-mfa-button');
        if (btn) {
          btn.click();
        }
      }
    });
  },
  methods: {
    async handleOktaCallback() {
      try {
        this.oktaLoading = true;

        const hash = window.location.hash;
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const urlParams = new URLSearchParams(queryString);

        const error = urlParams.get('error');
        if (error) throw new Error(`Okta error: ${error}`);

        const token = urlParams.get('token');
        if (!token) throw new Error('missing_parameters');

        // Store token first before anything else
        localStorage.setItem('feathers-jwt', token);

        // Set token in store so LOGIN_FINAL can use it
        this.$store.commit('users/SET_AUTH_TOKEN', token);

        const response = await this.$axios.get('/verify-jwt', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data && response.data._id) {
          this.user = response.data;
          await this.$store.dispatch('users/LOGIN_FINAL', this.user);
          // Wait for store to update isAuthenticated before routing
          await this.$nextTick();
          this.login_final();
        } else {
          throw new Error('Authentication verification failed');
        }
      } catch (error) {
        console.error('Okta callback error:', error);
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Authentication failed. Please try again.';
        this.snackbar = true;
        this.$router.push({ name: 'Login' });
      } finally {
        this.oktaLoading = false;
      }
    },
    noVenueLogout() {
      this.$store.dispatch('users/LOGOUT');
      this.snackbarColor = 'red';
      this.snackbarMessage = 'User has no available venue.';
      this.snackbar = true;
      this.buttonLoading = false;
    },
    // Login.vue - method login_username_password
    async login_username_password() {
      if (!this.$refs.loginForm.validate()) {
        return;
      }

      this.buttonLoading = true;

      try {
        this.user = await this.$store.dispatch('users/LOGIN', {
          email: this.email,
          password: this.password
        });

        // Case 1: First login AND MFA enabled -> Show QR code setup page
        if (this.user.firstLogin && this.user.isEnabledMFA) {
          // Store password temporarily for MFA setup
          this.$store.commit('users/SET_TEMP_PASSWORD', this.password);
          this.$router.push({ name: 'MFA Setup' });
        }
        // Case 2: MFA enabled (not first login) -> Show PIN input
        else if (this.user.isEnabledMFA && !this.user.firstLogin) {
          this.$router.push({ name: 'OTP Authenticator' });
          // this.showEnterPIN = true;
        }
        // Case 3: Normal login without MFA
        else {
          this.login_final();
        }
      } catch (error) {
        console.log(error);
        if (error && error.name === 'NotAuthenticated') {
          window.location.reload();
        } else {
          this.snackbarColor = 'red';
          this.snackbarMessage = 'Email or password is incorrect.';
          this.snackbar = true;
        }
      }

      this.buttonLoading = false;
    },
    async login_mfa() {
      this.buttonLoading = true;

      try {
        const success = await this.$store.dispatch('users/LOGIN_MFA', {
          pin: this.pin,
          user: this.user
        });

        if (success) {
          if (this.user.firstLogin && this.user.isEnabledMFA) {
            try {
              await this.$store.dispatch('users/UPDATE_FIRST_LOGIN', this.user._id);
              this.user = { ...this.user, firstLogin: false };
            } catch (updateError) {
              console.error('Failed to update firstLogin:', updateError);
            }
          }

          this.login_final();
        }
      } catch (error) {
        this.snackbarColor = 'red';
        this.snackbarMessage = error.message || 'Invalid PIN';
        this.snackbar = true;
      }

      this.buttonLoading = false;
    },
    login_final() {
      try {
        const jwt = localStorage.getItem('feathers-jwt');
        if (jwt) {
          const isSecure = window.location.protocol === 'https:';
          const maxAge = 2 * 60 * 60;
          const secureSuffix = isSecure ? '; Secure' : '';
          document.cookie = `feathers-jwt=${jwt}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;
        }

        const fromUrl = this.$store.getters['GET_FROM_URL'];

        if (fromUrl) {
          this.$store.commit('SET_FROM_URL', null);
          this.$router.push({ name: fromUrl });
        } else if (this.$route.query.from_url && this.$route.query.from_url !== '') {
          this.$router.go(this.$route.query.from_url);
        } else {
          const { license } = this.$store.state;

          if (license.hasError) {
            return this.$router.push({ name: 'Settings' });
          }

          if (this.user.isAdmin) {
            this.$router.push({ name: 'Users' });
            return;
          }

          // Check venue
          const selected = getSelectedVenue();
          const venues = this.$store.state.users.userVenues;
          let venueId;

          if (!selected) {
            if (venues.length) {
              venueId = venues[0]._id;
              sessionStorage.setItem(VENUE_STORAGE, JSON.stringify(venues[0]));
            } else {
              this.noVenueLogout();
              return;
            }
          } else {
            if (venues.length > 0) {
              const exists = venues.find(i => i._id === selected._id);

              if (exists) {
                venueId = selected._id;
              } else {
                sessionStorage.setItem(VENUE_STORAGE, JSON.stringify(venues[0]));
                venueId = venues[0]._id;
              }
            } else {
              this.noVenueLogout();
              return;
            }
          }

          if (this.$store.state.users.user.role === USER_ROLES.NONPUBLISH_USER) {
            this.$router.push({ name: 'Playlists', params: { venueId } });
          } else if (this.$store.state.users.user.role === USER_ROLES.REGULAR_USER) {
            this.$router.push({ name: 'User Profile' });
          } else {
            this.$router.push({ name: 'Devices', params: { venueId } });
          }
        }
      } catch (error) {
        console.log(error);

        throw error;
      }
    },
    login_groupdevices() {
      this.$router.push({ name: 'GroupDevices' });
    },
    async initiateOktaLogin() {
      this.oktaLoading = true;

      try {
        const requestData = {};
        if (this.$route.query.from_url) {
          requestData.from_url = this.$route.query.from_url;
        }

        const response = await this.$axios.post('/auth/login', requestData);

        window.location.href = response.data.authUrl;
      } catch (error) {
        console.error('Okta initiation error:', error);
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Failed to start Okta authentication';
        this.snackbar = true;
        this.oktaLoading = false;
      }
    },
    goToForgotPassword() {
      this.$router.push({ name: 'ForgotPassword' });
    }
  },
  mixins: [mixins]
};
</script>

<style scoped>
.login-container {
  display: flex;
  width: 100%;
  height: 100%;
}

.login-form-section {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50%;
}

.logo-container {
  position: absolute;
  top: 0;
  left: 0;
}

.welcome-text h2 {
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 30px;
  text-align: left;
  width: 100%;
}

.login-card {
  width: 100%;
  max-width: 400px;
  background-color: inherit;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}
/*

.custom-input {
  border-radius: 8px;
}

.custom-input >>> .v-input__control {
  min-height: 48px;
}

.custom-input >>> .v-text-field__details {
  margin-top: 4px;
}
*/

.forgot-password {
  text-align: right;
  margin-bottom: 24px;
}

.forgot-link {
  color: #00bfff;
  text-decoration: none;
  font-size: 14px;
}

.forgot-link:hover {
  text-decoration: underline;
}

.login-btn {
  background: linear-gradient(45deg, #00bfff, #1e90ff) !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
  height: 48px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.3);
}

.promo-section {
  flex: 1;
  background: url('/static/banner-login.png') no-repeat center center;
  background-size: contain;
  background-origin: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.forgot-password {
  text-align: right;
  margin-bottom: 24px;
}

.forgot-link {
  color: #00bfff;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.forgot-link:hover {
  text-decoration: underline;
  color: #0099cc;
}
</style>



// WEBPACK FOOTER //
// src/pages/Login.vue